import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { deriveViewModeFromLockInfo } from './editLockUtils';
import { DEFAULT_LOCK_MESSAGES, LockMessages, LockComponentType, Action } from './constants';
import { EntityLockedError } from 'src/models/common/EntityLockedError';
import { LockInfo } from 'src/models/common/LockInfo';
import apiService from 'src/services/ApiCallService';
import TPEAction from 'src/models/common/TPEAction';
import CONSTANTS from 'src/utils/constants';

/** Throw from `commitEdits` to abort the lock action with a yellow warning banner. */
export class LockActionValidationError extends Error {
    constructor(message = 'Please fix the errors on this page before saving.') {
        super(message);
        this.name = 'LockActionValidationError';
    }
}

type CommitEdits = () => Promise<void>;

const UnsavedChangesContext = React.createContext<Set<CommitEdits> | null>(null);

/**
 * Sections with unsaved local state register a `commitEdits` that runs when the lock-bar
 * Save/Confirm fires. Typically wraps the section's own Save-button function so state updates
 * stay identical between step-level Save and lock-bar Save.
 */
export function useReportsUnsavedChanges(commitEdits: CommitEdits): void {
    const registry = useContext(UnsavedChangesContext);
    // Latest closure is stashed via ref; the Set entry dispatches through it so identity is
    // stable while the newest closure is always invoked.
    const latest = useRef(commitEdits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        latest.current = commitEdits;
    });
    useEffect(() => {
        if (!registry) return;
        const entry: CommitEdits = () => latest.current();
        registry.add(entry);
        return () => {
            registry.delete(entry);
        };
    }, [registry]);
}

interface UseLockActionsParams {
    componentType: LockComponentType;
    entityId: string | undefined;
    currentUser: string;
    dispatch: React.Dispatch<TPEAction>;
    setViewModeAction: TPEAction;
    messageService: any;
    onRefetch: () => void;
    messages?: Partial<LockMessages>;
}

export interface LockActionsResult {
    handleEdit: () => void;
    handleSave: () => void;
    handleConfirm: () => void;
    handleRevert: () => void;
    actionInProgress: Action | null;
    /** Wrap host children in this so sections calling `useReportsUnsavedChanges` are registered. */
    UnsavedChangesContextProvider: React.ComponentType<{ children: React.ReactNode }>;
}

export function useLockActions({
    componentType,
    entityId,
    currentUser,
    dispatch,
    setViewModeAction,
    messageService,
    onRefetch,
    messages: customMessages,
}: UseLockActionsParams): LockActionsResult {
    const [actionInProgress, setActionInProgress] = useState<Action | null>(null);
    const messages = useMemo(() => ({ ...DEFAULT_LOCK_MESSAGES, ...customMessages }), [customMessages]);

    const registry = useRef<Set<CommitEdits>>(new Set()).current;
    const UnsavedChangesContextProvider = useMemo<React.ComponentType<{ children: React.ReactNode }>>(
        () => ({ children }) => (
            <UnsavedChangesContext.Provider value={registry}>
                {children}
            </UnsavedChangesContext.Provider>
        ),
        [registry],
    );

    const commitAllPendingEdits = useCallback(async (failureMessagePrefix: string): Promise<boolean> => {
        if (registry.size === 0) return true;
        try {
            await Promise.all([...registry].map(entry => entry()));
            return true;
        } catch (ex) {
            if (ex instanceof LockActionValidationError) {
                messageService.showWarningBanner(ex.message);
            } else {
                const msg = ex instanceof Error ? ex.message : String(ex);
                messageService.showErrorBanner(`${failureMessagePrefix}: ${msg}`);
            }
            return false;
        }
    }, [registry, messageService]);

    const handleEdit = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.EDIT);
        try {
            const response = await apiService.editEntityDraft(componentType, entityId);
            const json = await response.json();
            const lockInfo: LockInfo = json?.lockInfo;
            dispatch(setViewModeAction.withPayload(deriveViewModeFromLockInfo(lockInfo, currentUser)));
            messageService.showSuccessBanner(messages.editSuccess);
            onRefetch();
        } catch (ex) {
            if (ex instanceof EntityLockedError) {
                dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
                messageService.showWarningBanner(messages.lockedByOther(ex.lockedBy || 'another user'));
                onRefetch();
            } else {
                messageService.showErrorBanner(`${messages.editFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
            }
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, currentUser, dispatch, setViewModeAction, messageService, onRefetch, messages]);

    const handleSave = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.SAVE);
        if (!(await commitAllPendingEdits(messages.saveFailure))) {
            setActionInProgress(null);
            return;
        }
        dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
        try {
            await apiService.saveEntityDraftEdit(componentType, entityId);
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showSuccessBanner(messages.saveSuccess);
        } catch (ex) {
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showErrorBanner(`${messages.saveFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, dispatch, setViewModeAction, messageService, commitAllPendingEdits, messages]);

    const handleConfirm = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.CONFIRM);
        if (!(await commitAllPendingEdits(messages.confirmFailure))) {
            setActionInProgress(null);
            return;
        }
        dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
        try {
            await apiService.confirmEntityDraftEdit(componentType, entityId);
            messageService.showSuccessBanner(messages.confirmSuccess);
            onRefetch();
        } catch (ex) {
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showErrorBanner(`${messages.confirmFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, dispatch, setViewModeAction, messageService, onRefetch, commitAllPendingEdits, messages]);

    const handleRevert = useCallback(async () => {
        if (!entityId) return;
        setActionInProgress(Action.REVERT);
        dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.READ_ONLY));
        try {
            await apiService.revertEntityDraftEdit(componentType, entityId);
            messageService.showSuccessBanner(messages.revertSuccess);
            onRefetch();
        } catch (ex) {
            dispatch(setViewModeAction.withPayload(CONSTANTS.VIEW_MODE.EDITABLE));
            messageService.showErrorBanner(`${messages.revertFailure}: ${ex instanceof Error ? ex.message : String(ex)}`);
        } finally {
            setActionInProgress(null);
        }
    }, [componentType, entityId, dispatch, setViewModeAction, messageService, onRefetch, messages]);

    return { handleEdit, handleSave, handleConfirm, handleRevert, actionInProgress, UnsavedChangesContextProvider };
}
